/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { ChangeEvent, FC, FormEvent, useMemo, useState } from "react";
import {
  AppAlert,
  AuthenticationErrorMessage,
  ConfirmModal,
  ConnectButton,
  SocialProvider,
  ErrorMessage,
  IConstraintViolationMessage,
  IErrorMessage,
  LaddaButton,
  RecaptchaTerm,
  SupportLink,
  IBrowserError,
  getBrandStyleButton,
  useBrand,
} from "@advapacs/common";
import { TFunction } from "i18next";
import { getTypeDomain, openWindowOpenOauthURL } from "@src/utils/utils";
import { useAppDispatch } from "@src/config/store";
import { ILogin, IOrganisationSettings, ISaml } from "@src/types/login";
import LoginLaddaButton from "react-ladda-button";
import { Link, useLocation } from "react-router-dom";
import { SignUpPath } from "../signup";
import { LoginAction } from "./ducks/slices";
import { SelectDomainType } from "@src/components/SelectDomainType";
import { forgotOrganisation } from "@src/pages/login/ducks/operations";
import { ErrorAlert } from "@advapacs/common/src/components/Alert";
import axios from "axios";
import { SUPPORT_URL } from "@src/config/constants";
import BrandLaddaButton from "@src/components/BrandLaddaButton";

interface OriginalLoginFormLoadingProps {
  smalLoading?: boolean;
  loading?: boolean;
  captchaLoading?: boolean;
}

interface OriginalLoginFormFunctionsProps {
  setStateForm: (newState: ILogin) => void;
  onLogin: (e: FormEvent) => void;
  onNextOrganisation: () => void;
  setOrganisationId: (id: string) => void;
  onRedirectToSamlIdp: (sml: ISaml) => void;
}

interface OriginalLoginFormErrorsProps {
  organisationSettingsErrorMessage: ErrorMessage | null;
  twoFaErrorMessage?: ErrorMessage | null; // Errors returned from server side
  samlError: ErrorMessage | null;
  errorMessage: AuthenticationErrorMessage | null; // Errors returned from server side
  forgottenOrganisationErrorMessage?: ErrorMessage | null;
  browserError?: IBrowserError | null;
  turnstileError?: string;
}

interface OriginalLoginFormStateProps {
  // this props when checked type of domain is org
  showVerifyOrganisation: boolean;
  organisationId: string;
  stateForm: ILogin;
  organisationSettings: IOrganisationSettings | null;
  selectedSaml: ISaml;
  // this prop present settings have exit in system. (org found).
  isOrganisationLoginOriginal?: boolean;
  hasForgottenOrganisation?: boolean;
}

interface OriginalLoginFormProps {
  t: TFunction;
  formLoading: OriginalLoginFormLoadingProps;
  formErrors: OriginalLoginFormErrorsProps;
  formFunctions: OriginalLoginFormFunctionsProps;
  formState: OriginalLoginFormStateProps;
  socialErrorMessage?: IErrorMessage;
  shouldShowCurrentErrorMessage?: boolean;
  setSocialErrorMessage?: (err: IErrorMessage) => void;
}

export const OriginalLoginForm: FC<OriginalLoginFormProps> = ({
  t,
  formLoading,
  formFunctions,
  formErrors,
  formState,
  socialErrorMessage,
  setSocialErrorMessage,
  shouldShowCurrentErrorMessage = true,
}) => {
  const dispatch = useAppDispatch();
  const {
    errorMessage,
    samlError,
    twoFaErrorMessage,
    organisationSettingsErrorMessage,
    forgottenOrganisationErrorMessage,
    browserError,
    turnstileError,
  } = formErrors;
  const { loading, smalLoading, captchaLoading } = formLoading;
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );
  const {
    setStateForm,
    onLogin,
    onNextOrganisation,
    onRedirectToSamlIdp,
    setOrganisationId,
  } = formFunctions;

  const { brand } = useBrand();

  const {
    selectedSaml,
    isOrganisationLoginOriginal,
    organisationId,
    stateForm,
    organisationSettings,
    showVerifyOrganisation,
    hasForgottenOrganisation,
  } = formState;

  const [showForgotOrganisationModal, setShowForgotOrganisationModal] =
    useState<boolean>(false);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStateForm({ ...stateForm, [name]: value });
  };

  const backToIndividual = () => {
    const type = getTypeDomain();
    if (type === "individual") {
      dispatch(LoginAction.setShowVerifyOrganisation(false));
      return;
    }
    const host = window.location.host;
    const arrHost = host.split(".");
    const individualDomain = arrHost.splice(1, arrHost.length).join(".");
    window.location.href = `https://${individualDomain}`;
  };

  const shouldShowSignUpSelect = useMemo(
    () => !isOrganisationLoginOriginal && !organisationSettingsErrorMessage,
    [isOrganisationLoginOriginal, organisationSettingsErrorMessage]
  );

  const shouldShowSAMLLoginButton = useMemo(
    () =>
      organisationSettings?.samlIdps &&
      organisationSettings?.samlIdps.length > 0,
    [organisationSettings]
  );

  // condition: enable when domain type is org and still not call org setting in out system. it should show "enter_organisation_id"
  const shouldShowBoxFindOrg = useMemo(() => {
    return showVerifyOrganisation && !isOrganisationLoginOriginal;
  }, [showVerifyOrganisation, isOrganisationLoginOriginal]);

  // condition : found org in our system
  const shouldShowBackToIndividualBtn = useMemo(() => {
    return isOrganisationLoginOriginal;
  }, [isOrganisationLoginOriginal]);

  const getOrganiErrorMessage = useMemo(() => {
    if (forgottenOrganisationErrorMessage?.message)
      return forgottenOrganisationErrorMessage.message;
    if (forgottenOrganisationErrorMessage?.code === "422") {
      return (forgottenOrganisationErrorMessage as IConstraintViolationMessage)
        ?.errors[0]?.message;
    }
  }, [forgottenOrganisationErrorMessage]);

  const showSocialConnectButtons = useMemo(() => {
    return !isOrganisationLoginOriginal && !showVerifyOrganisation;
  }, [isOrganisationLoginOriginal, showVerifyOrganisation]);

  // social login
  const handleSigninWithProvider = async (provider: string) => {
    try {
      const rs = await axios.get(`/auth/oauth/redirect/${provider}/login`);
      openWindowOpenOauthURL(rs?.data?.redirectTo);
    } catch (error) {
      setSocialErrorMessage(error?.response?.data);
    }
  };

  const brandStyleButton = getBrandStyleButton(brand);
  const btnClass = `btn ${
    Object.keys(brandStyleButton).length ? "" : "btn-primary"
  } btn-block btn-ladda btn-ladda-spinner`;

  return (
    <>
      {showForgotOrganisationModal && (
        <ConfirmModal
          loading={loading}
          show={showForgotOrganisationModal}
          onClose={() => {
            setShowForgotOrganisationModal(false);
          }}
          confirm={() => {
            dispatch(forgotOrganisation(stateForm?.email));
            setShowForgotOrganisationModal(false);
          }}
          cancel={() => {
            setShowForgotOrganisationModal(false);
          }}
          header={t("send_organisation_id")}
          cancelBtnName={t("cancel")}
          confirmBtnName={t("send")}
          confirmBtnVariant="primary"
          children={
            <>
              <div className="text-center pb-2">{t("enter_email")}</div>
              <span className="mr-2">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control search"
                    name="email"
                    placeholder={t("email")}
                    onChange={handleInputChange}
                  />
                </div>
              </span>
            </>
          }
        />
      )}

      <form autoComplete="off">
        <h4 className="mb-0 text-center font-weight">{t("cp_login")}</h4>
        <div className="form-group text-center">
          {shouldShowSignUpSelect && (
            <>
              <span className="d-inline-block">
                <span> {t("account.dont_have_account")}</span>
                <Link className="ml-1" to={SignUpPath.SELECT}>
                  {t("signup")}
                </Link>
              </span>
            </>
          )}
          {isOrganisationLoginOriginal && (
            <span className="d-block text-muted">
              {t("login:enter_credentials")}
            </span>
          )}
        </div>
        {!isOrganisationLoginOriginal && (
          <SelectDomainType
            isOrg={showVerifyOrganisation}
            onIndividualClick={() => {
              dispatch(LoginAction.setShowVerifyOrganisation(false));
            }}
            onOrgClick={() => {
              dispatch(LoginAction.setShowVerifyOrganisation(true));
            }}
          />
        )}
        {showSocialConnectButtons && (
          <>
            <ConnectButton
              lable="Sign in"
              className="d-block w-100 m-b-sm"
              type={SocialProvider.GOOGLE}
              onConnect={() => handleSigninWithProvider(SocialProvider.GOOGLE)}
            />
            <ConnectButton
              lable="Sign in"
              className="d-block w-100 m-b-sm"
              type={SocialProvider.MICROSOFT}
              onConnect={() => {
                // redirect to google oauth to get information
                // to send callback new steps before signup new accounts.
                handleSigninWithProvider("azure");
              }}
            />
            <div className="middle-line p-t-xxs">
              <span className="text-muted">OR</span>
            </div>
          </>
        )}
        <div className="form-group text-center">
          {organisationSettingsErrorMessage && (
            <div className="form-group">
              <div className="alert bg-warning text-white alert-styled-left error-container">
                {t("organisation_notfound")}
              </div>
            </div>
          )}
          {shouldShowBoxFindOrg && (
            <>
              <div className="form-group form-group-feedback form-group-feedback-left">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("organisation_id")}
                  name="username"
                  onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      e.preventDefault();
                      onNextOrganisation();
                      return;
                    } // enter
                  }}
                  id="usernameInput"
                  onChange={(e) =>
                    setOrganisationId(
                      e.target.value ? e.target.value.toLowerCase() : ""
                    )
                  }
                  value={organisationId}
                />
                <div className="form-control-feedback">
                  <i className="fas fa-sitemap text-muted" />
                </div>
              </div>
              {getOrganiErrorMessage && (
                <AppAlert.ErrorAlert
                  dismissible
                  error={getOrganiErrorMessage}
                />
              )}
              {hasForgottenOrganisation && (
                <div className="form-group">
                  <div className="alert bg-success text-white alert-styled-left error-container">
                    {t("get_organisation_id_success")}
                  </div>
                </div>
              )}

              <div className="form-group">
                <BrandLaddaButton
                  className={btnClass}
                  onClickHandler={onNextOrganisation}
                  type="button"
                  btnName={t("general.continue")}
                  disabled={!organisationId}
                  style={brandStyleButton}
                />
              </div>

              {!!organisationSettingsErrorMessage && (
                <div className="form-group">
                  <LaddaButton
                    className="btn-light btn-block"
                    onClickHandler={backToIndividual}
                    type="button"
                    btnName={t("back_to_individual_login")}
                  />
                </div>
              )}
              <>
                <div className="form-group text-center m-b-xxs">
                  <a
                    className="btn-block cursor-pointer"
                    onClick={() => {
                      setShowForgotOrganisationModal(true);
                    }}
                  >
                    {t("forgot_your_organisation")}
                  </a>
                </div>
              </>
            </>
          )}
          {(!showVerifyOrganisation || isOrganisationLoginOriginal) && (
            <>
              <div className="form-group form-group-feedback form-group-feedback-left">
                <input
                  type="text"
                  className="form-control"
                  placeholder={
                    getTypeDomain() === "individual"
                      ? t("email")
                      : t("username")
                  }
                  name="username"
                  id="usernameInput"
                  onChange={handleInputChange}
                  value={stateForm.username}
                />
                <div className="form-control-feedback">
                  <i className="icon-user text-muted" />
                </div>
              </div>
              <div className="form-group form-group-feedback form-group-feedback-left">
                <input
                  type="password"
                  className="form-control"
                  placeholder={t("password")}
                  name="password"
                  id="passwordInput"
                  onChange={handleInputChange}
                  value={stateForm.password}
                  autoComplete="new-password"
                />
                <div className="form-control-feedback">
                  <i className="icon-lock2 text-muted" />
                </div>
              </div>
              <div className="form-group">
                <LoginLaddaButton
                  className={btnClass}
                  onClick={onLogin}
                  data-style="slide-right"
                  type="submit"
                  disabled={
                    !stateForm?.password ||
                    !stateForm?.username ||
                    loading ||
                    captchaLoading
                  }
                  loading={loading || captchaLoading}
                  style={brandStyleButton}
                >
                  <span className="ladda-label">
                    {t("login")}
                    <i className="icon-circle-right2 ml-2" />
                  </span>
                </LoginLaddaButton>
              </div>

              {shouldShowBackToIndividualBtn && (
                <div className="form-group">
                  <LaddaButton
                    className="btn-light btn-block"
                    onClickHandler={backToIndividual}
                    type="button"
                    btnName={t("back_to_individual_login")}
                  />
                </div>
              )}

              {twoFaErrorMessage && (
                <div className="form-group">
                  <div className="alert bg-warning text-white alert-styled-left error-container">
                    {twoFaErrorMessage.message}
                  </div>
                </div>
              )}
              {!twoFaErrorMessage &&
                errorMessage &&
                shouldShowCurrentErrorMessage && (
                  <div className="form-group">
                    <ErrorAlert
                      error={
                        // @ts-ignore
                        errorMessage?.params?.hint
                          ? t(
                              "error_message_server." +
                                // @ts-ignore
                                errorMessage.params?.hint
                            )
                          : errorMessage
                      }
                      className="alert bg-warning text-white alert-styled-left error-container"
                    />
                  </div>
                )}
              {socialErrorMessage && (
                <AppAlert.ErrorAlert
                  error={socialErrorMessage}
                  dismissible
                  onClose={() => {
                    if (setSocialErrorMessage) setSocialErrorMessage(null);
                  }}
                />
              )}

              {browserError && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  message={browserError.message}
                />
              )}

              {turnstileError && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  message={turnstileError}
                />
              )}
              {!isOrganisationLoginOriginal && (
                <>
                  <div className="form-group text-center m-b-xs">
                    <Link className="btn-block" to="/ForgotPassword">
                      {t("forgot_your_password")}
                    </Link>
                  </div>
                </>
              )}

              {shouldShowSAMLLoginButton && (
                <>
                  <div className="middle-line">
                    <span className="text-muted">{t("or")}</span>
                  </div>
                  {organisationSettings?.samlIdps.map((saml) => (
                    <LaddaButton
                      className="btn-primary btn-block"
                      onClickHandler={() => onRedirectToSamlIdp(saml)}
                      type="submit"
                      loading={
                        smalLoading && selectedSaml?.idpId === saml.idpId
                      }
                      btnName={t("login_via_saml", { saml: saml.name })}
                    />
                  ))}
                  {samlError && (
                    <div className="form-group mt-2">
                      <div className="alert bg-warning text-white alert-styled-left error-container">
                        {samlError?.message}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          <SupportLink
            link={SUPPORT_URL}
            className={`text-center w-100 m-b-xs ${
              shouldShowSAMLLoginButton ? "m-t-md" : ""
            }`}
            noTranslation={false}
          />
          <RecaptchaTerm />
        </div>
      </form>
      {queryParams.get("eulaWarnMessage") && (
        <AppAlert.SimpleAlert
          variant="warning"
          message={t("form:clinician_portal.eula_message_access_not_allowed")}
          dismissible
        />
      )}
    </>
  );
};
